import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, TableCell, Tooltip, Typography } from "@mui/material";
import { ScanText, UserPlus } from "lucide-react";
import DataTable from "bundles/Internal/components/common/DataTable";
import CvTableFilters from "bundles/Internal/components/cv_vault/CvTableFilters";
import { STATUS_TONE } from "bundles/Internal/components/cv_vault/VaultListRow";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "status", label: "Status" },
  { key: "ocr", label: "OCR" },
  { key: "owner", label: "Uploaded by" },
  { key: "created_at", label: "Uploaded" },
];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

function formatCvCell(item, key) {
  const parsed = item?.parsed || {};

  switch (key) {
  case "id":
    return item?.id ? `#${item.id}` : "";
  case "name":
    return [parsed.first_name, parsed.last_name].filter(Boolean).join(" ") || item?.filename || "";
  case "email":
    return parsed.email || "—";
  case "phone":
    return parsed.phone || "—";
  case "owner":
    return item?.owner?.name || "—";
  case "created_at":
    return formatDate(item?.created_at);
  default:
    return "";
  }
}

const CvTable = ({
  items,
  filters,
  onFiltersChange,
  owners,
  onQueryChange,
  onConvert,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading,
}) => {
  const navigate = useNavigate();

  const renderCell = useCallback(
    (item, key) => {
      if (key === "status") {
        const tone = STATUS_TONE[item?.status] || STATUS_TONE.uploaded;
        return (
          <Chip
            size="small"
            label={item?.stalled ? "Stopped responding" : tone.label}
            sx={{
              height: 20,
              fontSize: 10.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "8px",
              color: tone.color,
              backgroundColor: tone.bg,
            }}
          />
        );
      }

      if (key === "ocr") {
        if (item?.status === "needs_ocr") {
          return (
            <Typography sx={{ fontSize: 13, color: "hsl(35 92% 30%)" }}>Needs OCR</Typography>
          );
        }
        if (!item?.ocr_engine) return "No";
        return (
          <Tooltip arrow title={item.ocr_engine}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <ScanText size={13} />
              {item.ocr_pages ? `${item.ocr_pages} page${item.ocr_pages === 1 ? "" : "s"}` : "Scanned"}
            </Box>
          </Tooltip>
        );
      }

      if (key === "name") {
        return (
          <Box
            component="span"
            sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}
          >
            {formatCvCell(item, key)}
          </Box>
        );
      }

      return formatCvCell(item, key);
    },
    [],
  );

  return (
    <DataTable
      title="CV table"
      totalLabel={(count) => `${count.toLocaleString()} total CVs`}
      emptyTitle="No CVs found"
      emptySubtitle="Try adjusting your search criteria"
      columns={COLUMNS_CONSTANTS}
      data={items}
      renderCell={renderCell}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by name, email, phone, filename or CV text..."
      filtersComponent={
        filters && onFiltersChange ? (
          <CvTableFilters filters={filters} onFiltersChange={onFiltersChange} owners={owners} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
      tailHeadColumns={[""]}
      renderTailCells={(item) => (
        <TableCell key="actions" sx={{ whiteSpace: "nowrap", py: 1.5 }}>
          {item?.candidate_id ? (
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/internal/candidates/${item.candidate_id}`);
              }}
              sx={{
                color: "hsl(150 38% 17%)",
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {`Candidate #${item.candidate_id}`}
            </Box>
          ) : (
            <Button
              size="small"
              disableRipple
              disabled={item?.status !== "parsed"}
              startIcon={<UserPlus size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onConvert?.(item);
              }}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                borderRadius: "calc(-2px + 0.75rem)",
                px: 1.5,
                color: "hsl(80 97% 72%)",
                backgroundColor: "hsl(150 38% 17%)",
                "& .MuiButton-startIcon": { mr: 0.75 },
                "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
                "&.Mui-disabled": { backgroundColor: "hsl(140 8% 92%)", color: "hsl(150 8% 55%)" },
              }}
            >
              Create candidate
            </Button>
          )}
        </TableCell>
      )}
    />
  );
};

export default CvTable;
